import * as zip from "@zip.js/zip.js";

const UGC_LIBRARY_KEY = "choirplayer:ugcLibrarySongs";
const UGC_BUCKET_BASE = "https://choirplayerugc.s3.amazonaws.com/ugc-uploads";


export function getCurrentUgcUserEmail() {
  try {
    return (
      window.localStorage.getItem("UGC_USER_EMAIL") ||
      "anonymous-user"
    );
  } catch (e) {
    return "anonymous-user";
  }
}

export function getEncodedUgcUserEmail() {
  return encodeURIComponent(getCurrentUgcUserEmail());
}

export function getUgcSongLibraryUrlForEmail(email) {
  return `${UGC_BUCKET_BASE}/${encodeURIComponent(email)}/ugc-songlist.json`;
}

export function getCurrentUgcSongLibraryUrl() {
  return getUgcSongLibraryUrlForEmail(getCurrentUgcUserEmail());
}



export function isUgcTestingEnabled() {
  try {
    return window.localStorage.getItem("ugcTesting") === "1";
  } catch (e) {
    return false;
  }
}

export function getUgcLibrarySongs() {
  try {
    const raw = window.localStorage.getItem(UGC_LIBRARY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}


export function saveUgcLibrarySongs(songs) {
  window.localStorage.setItem(UGC_LIBRARY_KEY, JSON.stringify(songs || []));
}

export function clearUgcLocalData() {
  try {
    window.localStorage.removeItem(UGC_LIBRARY_KEY);
    window.localStorage.removeItem("UGC_USER_EMAIL");

    Object.keys(window.localStorage).forEach((key) => {
      if (key.startsWith("choirplayer:metadata:")) {
        window.localStorage.removeItem(key);
      }
    });
  } catch (e) {
    console.warn("Could not clear UGC local data", e);
  }
}



export async function createUgcStemZip(stemAssignments) {
  const blobWriter = new zip.BlobWriter("application/zip");
  const zipWriter = new zip.ZipWriter(blobWriter);

  const stemNames = ["BT", "Sopr", "Alto", "Tenor", "Bass"];
  const uploadedStemNames = [];

  for (const stemName of stemNames) {
    const file = stemAssignments?.[stemName];

    if (!file) continue;

    const ext = file.name.split(".").pop() || "mp3";

    uploadedStemNames.push(stemName);

    await zipWriter.add(
      `${stemName}.${ext}`,
      new zip.BlobReader(file)
    );
  }

  await zipWriter.close();

  return {
    zipBlob: await blobWriter.getData(),
    uploadedStemNames,
  };
}

export async function fetchRemoteUgcLibrarySongsForEmail(email) {
  const baseUrl = getUgcSongLibraryUrlForEmail(email);
  const separator = baseUrl.includes("?") ? "&" : "?";
  const url = `${baseUrl}${separator}cb=${Date.now()}`;

  try {
    console.log("[UGC] fetchRemoteUgcLibrarySongs FRESH url =", url);

    const response = await fetch(url, {
      cache: "no-store",
    });

	if (!response.ok) {
	  // S3 may return 403 rather than 404 when the object does not exist
	  // or when ListBucket is not allowed. For a new user this just means
	  // they do not have a UGC songlist yet.
	  if (response.status === 403 || response.status === 404) return [];

	  throw new Error(`Could not fetch UGC song library: ${response.status}`);
	}

    const json = await response.json();
    const songs = Array.isArray(json) ? json : [];

    const normalisedSongs = songs.map(normaliseUgcSong);

    console.log("[UGC] fetched remote UGC songs =", normalisedSongs.map((song) => ({
      songId: song.songId,
      mix: song.mix,
      chaptersCount: song.chapters?.length || 0,
      lyrics: song.lyrics,
      lyricsUrl: song.lyricsUrl,
    })));

    return normalisedSongs;
  } catch (e) {
    console.error("Could not load UGC song library", e);
    return [];
  }
}

export async function fetchRemoteUgcLibrarySongs() {
  return fetchRemoteUgcLibrarySongsForEmail(getCurrentUgcUserEmail());
}


export async function uploadUgcSongLibraryJson(songs) {
  const filename = `${getCurrentUgcUserEmail()}/ugc-songlist.json`;

  const blob = new Blob(
    [JSON.stringify(songs, null, 2)],
    { type: "application/json" }
  );

  return uploadUgcFileToS3(blob, filename, "application/json");
}


export async function uploadUgcLyricsJson(songId, lyrics) {
  const filename = `${getCurrentUgcUserEmail()}/${songId}-lyrics.json`;

  const json = {
    songId,
    lyrics,
  };

  const blob = new Blob(
    [JSON.stringify(json, null, 2)],
    { type: "application/json" }
  );

  return uploadUgcFileToS3(blob, filename, "application/json");
}



export async function updateUgcSongInLibrary(songId, songPatch) {
  let songs = await fetchRemoteUgcLibrarySongs();

  if (!songs.length) {
    songs = getUgcLibrarySongs();
  }


  const nextSongs = songs.map((song) =>
	song.songId === songId
	  ? {
		  ...song,
		  ...songPatch,

		  // keep compatibility with existing ChoirPlayer format
		  ...(songPatch.lyricsUrl
			? {
				lyrics: songPatch.lyricsUrl,
				lyricsUrl: songPatch.lyricsUrl,
			  }
			: {}),
		}
	  : song
  );


  saveUgcLibrarySongs(nextSongs);
  await uploadUgcSongLibraryJson(nextSongs);

  return nextSongs.find((song) => song.songId === songId);
}







export async function requestUgcUploadUrl(filename, contentType) {
  const response = await fetch("https://www.choirplayer.com/wp-json/myplugin/v1/ugc-presign", {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
    },

	body: new URLSearchParams({
	  filename,
	  contentType,
	}),


  });

  const json = await response.json();

  if (!response.ok || !json.uploadUrl || !json.publicUrl) {
    throw new Error(json.message || "Could not get UGC upload URL");
  }

  return json;
}

export async function uploadUgcFileToS3(blob, filename, contentType) {
  const { uploadUrl, publicUrl } = await requestUgcUploadUrl(filename, contentType);

  const uploadResponse = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": contentType,
    },
    body: blob,
  });

  if (!uploadResponse.ok) {
    throw new Error(`S3 upload failed: ${uploadResponse.status}`);
  }

  return publicUrl;
}


export async function uploadUgcSetlistImageToS3(file, email = getCurrentUgcUserEmail()) {
  if (!file) {
    throw new Error("No setlist image file selected");
  }

  if (!email || email === "anonymous-user") {
    throw new Error("You must be logged in to upload a setlist image");
  }

  const allowedImageTypes = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "image/gif": "gif",
  };

  const contentType = allowedImageTypes[file.type]
    ? file.type
    : "image/jpeg";

  const ext = allowedImageTypes[contentType];
  const imageId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const filename = `${email}/setlist-images/${imageId}.${ext}`;

  return uploadUgcFileToS3(file, filename, contentType);

}

export async function uploadUgcZipToS3(zipBlob, filename) {
  return uploadUgcFileToS3(zipBlob, filename, "application/zip");
}




export function buildUgcSong({ songId, mix, len, pathzip, stemNames, existingSong }) {
  const names = Array.isArray(stemNames) && stemNames.length
    ? stemNames
    : ["BT", "Sopr", "Alto", "Tenor", "Bass"];

  const previousSong = existingSong || {};

  const lyricsUrl =
    previousSong.lyricsUrl ||
    previousSong.lyrics ||
    "";

  return {
    ...previousSong,

    songId,
    mix,
    title: mix,
    artist: previousSong.artist || "User uploaded",
    len: Number(len || 0),
    duration: Number(len || 0),
    key: previousSong.key || "",
    decade: previousSong.decade || "",
    genre: previousSong.genre || "UGC",
    pathzip,

    chapters: Array.isArray(previousSong.chapters) ? previousSong.chapters : [],

    // keep both fields because existing ChoirPlayer songlists may use either
    lyrics: lyricsUrl,
    lyricsUrl,

    tracks: names.map((name) => ({ name })),
    tracknames: names.map((name) => ({ name })),
  };
}

export function normaliseUgcSong(song) {
  const lyricsUrl = song?.lyricsUrl || song?.lyrics || "";

  const normalisedSong = {
    ...song,
    lyrics: lyricsUrl,
    lyricsUrl,
    chapters: Array.isArray(song?.chapters) ? song.chapters : [],
  };

  console.log("[UGC] normaliseUgcSong =", {
    songId: normalisedSong.songId,
    mix: normalisedSong.mix,
    chaptersCount: normalisedSong.chapters.length,
    lyrics: normalisedSong.lyrics,
    lyricsUrl: normalisedSong.lyricsUrl,
  });

  return normalisedSong;
}

export async function fetchRemoteUgcSongById(songId) {
  const songs = await fetchRemoteUgcLibrarySongs();
  const foundSong = songs.find((song) => String(song.songId) === String(songId));

  console.log("[UGC] fetchRemoteUgcSongById =", {
    requestedSongId: songId,
    found: !!foundSong,
    chaptersCount: foundSong?.chapters?.length || 0,
    lyrics: foundSong?.lyrics,
    lyricsUrl: foundSong?.lyricsUrl,
  });

  return foundSong || null;
}



